import { IController } from 'angular';
import { extend, includes, map } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ISubscription } from 'rxjs/Subscription';
import { BatchEditService } from 'Src/ng2/shared/services/batch-edit-service/batch-edit-service';
import nvSdcModule from '../../nv.sdcModule.module';
import { AssignSupportModalService } from './../../../../ng2/shared/modals/assign-support/assign-support-modal.service';
import { IBaseModalData, ModalsService } from './../../../../ng2/shared/modals/modals.service';

const validStateNames = [
  'main.school.new-sdc.grad-planning.grid',
  'main.school.new-sdc.regents-planning.grid',
  'main.school.new-sdc.regents-prep.grid',
  'main.school.new-sdc.credit-gaps.grid',
  'main.school.new-sdc.attendance.grid',
  'main.school.new-sdc.marking-period.grid',
  'main.school.new-sdc.default-sdc.grid',
];
export class GridToolbarCtrl implements IController {
  static $inject = [
    '$scope',
    '$rootScope',
    '$mdDialog',
    'SDCStore',
    'sdcActions',
    'StudentReportModal',
    'NoteModal',
    '$state',
    'ObjectCache',
    'PlannedDiplomaOptionsModal',
    'UserRolePermissionsForModelService',
    'ModalsService',
    'AssignSupportModalService',
    'BatchEditService',
  ];

  // Bindings
  private isRestricted;
  private hasPendingSaves;
  private _modalResolved;
  private filter;
  private sort;
  private cellStatus;
  private numSelectedStudents;
  private refreshColumns;
  private currentUser;
  public filterWidgetIsVisible;

  openBatchEditModalSubscription: ISubscription;
  destroy$: Subject<boolean> = new Subject<boolean>();
  studentIds: string[];
  schoolId: string;

  constructor(
    private $scope,
    private $rootScope,
    private $mdDialog,
    private SDCStore,
    private sdcActions,
    private StudentReportModal,
    private NoteModal,
    private $state,
    private ObjectCache,
    private PlannedDiplomaOptionsModal,
    private UserRolePermissionsForModelService,
    private modalsService: ModalsService,
    private assignSupportModalService: AssignSupportModalService,
    private batchEditService: BatchEditService,
  ) {}

  $onInit() {
    // For permissioning
    const PERMISSIONING_MODEL_NAME = 'IM_STUDENT';
    const canEdit = this.UserRolePermissionsForModelService.canEditPartial(PERMISSIONING_MODEL_NAME);
    const obj = null; // we are not checking permissioning for an instance, but for the MODEL as a whole
    this.isRestricted = !canEdit(obj, this.currentUser);
    const currName = this.$state.current.name;
    this.filterWidgetIsVisible = includes(validStateNames, currName);

    this.hasPendingSaves = this.SDCStore.hasPendingSaves();

    // // sent from GRID CELL editing
    this.$rootScope.$on('cellStatus', (e, data) => {
      if (data) this.cellStatus = data;
    });

    // // sent from single cell edit, single student view, or bulk edit
    this.$rootScope.$on('refetchRequest', (ev, data) => {
      if (data) this.cellStatus = data;
    });

    this.$scope.$listenTo(this.SDCStore, ['ui', 'pendingSaves'], () => {
      this.hasPendingSaves = this.SDCStore.hasPendingSaves();
    });

    this.$scope.$listenTo(this.SDCStore, ['selectedStudentIds'], () => {
      this.numSelectedStudents = this.SDCStore.getSelectedStudentIds().length;
    });

    this.$scope.selectAllStudents = () => {
      this.sdcActions.selectFilteredStudents();
    };

    this.$scope.unselectAllStudents = () => {
      this.sdcActions.unselectAllStudents();
    };
  }
  // Functions

  $onDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  openModalColumnsTab = ev => {
    this.$scope.MODE = 'SHOW_HIDE_COLUMNS';
    this._openModal(ev);
  };

  _openModal = ev => {
    this.$mdDialog
      .show({
        locals: {
          refreshColumns: this.refreshColumns,
        },
        isolateScope: false,
        scope: this.$scope, // pass parent scope to dialog
        preserveScope: true, // must be true
        disableParentScroll: true,
        hasBackdrop: true,
        template: require('../modal/grid-modal.html'),
        parent: angular.element(document.body),
        targetEvent: ev,
        clickOutsideToClose: true,
        controller: 'gridModalCtrl',
        controllerAs: 'gridModalCtrl',
      })
      .then(this._modalResolved);
  };

  openStudentReportModal(event, reportName) {
    this.StudentReportModal.show(this.SDCStore.getSelectedStudentIds(), reportName);
  }

  openPlannedDiplomaOptionsModal = () => {
    this.PlannedDiplomaOptionsModal.show(this.SDCStore.getSelectedStudentIds());
  };

  openNoteModal = () => {
    const schoolId = this.SDCStore.getSchool()._id;
    const studentIds = this.SDCStore.getSelectedStudentIds();
    const opts = {
      schoolId,
      studentId: studentIds,
      currentUser: this.currentUser,
    };
    this.NoteModal.show(opts);
  };

  sendToBatchEditModal(): void {
    this.schoolId = this.SDCStore.getSchool()._id;
    this.studentIds = this.SDCStore.getSelectedStudentIds();
    const baseModalData: IBaseModalData = {
      studentIds: this.studentIds,
      isProfileMode: false,
    };
    this.openBatchEditModalSubscription = this.modalsService
      .openBatchEditModal(baseModalData)
      .afterClosed()
      .pipe(
        tap(res => {
          const params = {
            studentIds: this.studentIds,
            schoolId: this.schoolId,
            patch: res,
            destroy$: this.destroy$,
          };
          if (res) this.batchEditService.batchEditStudents(params);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  openStudentSupportModal = () => {
    const schoolId = this.SDCStore.getSchool()._id;
    const studentIds = this.SDCStore.getSelectedStudentIds();

    this.modalsService
      .openAssignSupportModal({ schoolId, studentIds })
      .afterClosed()
      .subscribe(selectedSupport => {
        if (selectedSupport) {
          this.assignSupportModalService.bulkAssignStudentSupport({ support: selectedSupport, studentIds });
        }
      });
  };

  openStudentActivitiesModal = () => {
    const schoolId = this.SDCStore.getSchool()._id;
    const studentIds = this.SDCStore.getSelectedStudentIds();

    this.modalsService
      .openStudentActivitiesModal({
        schoolId,
        studentIds,
        mode: 'CREATE',
        isProfileMode: false,
        currentUser: this.currentUser,
      })
      .afterClosed()
      .subscribe((data: any) => {
        // allows user to create new activity and then return to the student activities modal
        if (data && data.returnTo === 'StudentActivitiesModal') {
          this.modalsService
            .openActivitiesModal({
              mode: 'CREATE',
              schoolId: this.schoolId,
              isProfileMode: false,
            })
            .afterClosed()
            .subscribe(payload => {
              if (data && data.returnTo === 'StudentActivitiesModal') {
                this.openStudentActivitiesModal();
              }
            });
        }
      });
  };

  viewProfilesOfSelectedStudents() {
    const sdcStoreFilter = this.SDCStore.getFilter();

    const selectedStudents = {
      _id: map(this.SDCStore.getSelectedFlattenedStudents(), '_id'),
    };

    const currentFilter = extend({}, selectedStudents, sdcStoreFilter);
    this.filter = this.ObjectCache.cacheObject(currentFilter);

    const sdcStoreSort = this.SDCStore.getSort();
    this.sort = this.ObjectCache.cacheObject(sdcStoreSort);

    // Pass grid/sort to sdc route as query params
    const sdcKey = this.SDCStore.getSdcKeyFromUiState();
    const url = `main.school.new-sdc.${sdcKey}.profile`;
    this.$state.go(url, { filter: this.filter, sort: this.sort });
  }

  _cancel = () => {
    this.$mdDialog.cancel('cancel');
  };
}

angular.module(nvSdcModule).controller('gridToolbarCtrl', GridToolbarCtrl);
